/**
 * Ship/unit constants. Every number traces to the manual mirror:
 * docs/reference/site/manual/damage_chart.md (and is TESTED against it —
 * tests/damage-chart.test.ts parses that file and compares every cell).
 * FT (Fort) is included this time; legacy v1/v2 skipped it.
 */
import type { GameState, Ship, ShipType, Side } from './state';

/** Attack/defense keys: ships (incl. Fort) plus the submerged/surfaced sub distinction. */
export type UnitKey = ShipType | 'SSS';

export interface UnitSpec {
  name: string;
  cost: number;
  move: number;
  hp: number;
}

export const UNITS: Record<ShipType, UnitSpec> = {
  CV: { name: 'Carrier', cost: 21, move: 2, hp: 7 },
  BB: { name: 'Battleship', cost: 20, move: 2, hp: 12 },
  CR: { name: 'Cruiser', cost: 15, move: 2, hp: 9 },
  SS: { name: 'Submarine', cost: 12, move: 2, hp: 6 }, // submerged 2, surfaced 3 (moveAllowance)
  DD: { name: 'Destroyer', cost: 10, move: 3, hp: 6 },
  PT: { name: 'Patrol Boat', cost: 4, move: 4, hp: 3 },
  AT: { name: 'Armed Transport', cost: 8, move: 3, hp: 3 },
  TR: { name: 'Transport', cost: 3, move: 2, hp: 3 },
  FT: { name: 'Fort', cost: 7, move: 0, hp: 10 },
};

/**
 * Damage chart, attacker row → defender column, verbatim from damage_chart.md.
 * SS = submerged sub, SSS = surfaced sub (identical rows as attacker, distinct as defender).
 */
export const DMG: Record<UnitKey, Record<UnitKey, number>> = {
  CV:  { CV: 6, BB: 5, CR: 5, SS: 2, DD: 5, PT: 3, AT: 3, TR: 6, FT: 1, SSS: 5 },
  BB:  { CV: 5, BB: 4, CR: 5, SS: 0, DD: 6, PT: 7, AT: 7, TR: 7, FT: 5, SSS: 6 },
  CR:  { CV: 5, BB: 3, CR: 4, SS: 3, DD: 5, PT: 6, AT: 6, TR: 6, FT: 4, SSS: 5 },
  SS:  { CV: 5, BB: 4, CR: 5, SS: 0, DD: 2, PT: 1, AT: 3, TR: 3, FT: 0, SSS: 2 },
  DD:  { CV: 3, BB: 1, CR: 2, SS: 6, DD: 3, PT: 4, AT: 4, TR: 4, FT: 1, SSS: 3 },
  PT:  { CV: 0, BB: 0, CR: 0, SS: 3, DD: 1, PT: 2, AT: 2, TR: 3, FT: 0, SSS: 1 },
  AT:  { CV: 0, BB: 0, CR: 0, SS: 0, DD: 1, PT: 2, AT: 2, TR: 3, FT: 0, SSS: 1 },
  TR:  { CV: 0, BB: 0, CR: 0, SS: 0, DD: 0, PT: 0, AT: 0, TR: 1, FT: 0, SSS: 0 },
  FT:  { CV: 4, BB: 3, CR: 3, SS: 0, DD: 6, PT: 1, AT: 3, TR: 6, FT: 0, SSS: 6 },
  SSS: { CV: 5, BB: 4, CR: 5, SS: 0, DD: 2, PT: 1, AT: 3, TR: 3, FT: 0, SSS: 2 },
};

/** Fire order: larger ships fire first (manual: game-rules). FT slotted by its
 *  absorb value (10, between BB 12 and CR 9) — the manual doesn't rank forts. */
export const FIRE_ORDER: Record<ShipType, number> = {
  CV: 0, BB: 1, FT: 1.5, CR: 2, SS: 3, DD: 4, PT: 5, AT: 6, TR: 7,
};

export function isTransportClass(s: Pick<Ship, 'type'>): boolean {
  return s.type === 'TR' || s.type === 'AT';
}

/** chart key a unit defends as */
export function defKey(s: Pick<Ship, 'type' | 'submerged'>): UnitKey {
  return s.type === 'SS' ? (s.submerged ? 'SS' : 'SSS') : s.type;
}

/** chart key a unit attacks as (sub rows are identical either way; legacy used SS) */
export function atkKey(s: Pick<Ship, 'type'>): UnitKey {
  return s.type;
}

export function addShip(g: GameState, side: Side, type: ShipType, c: number, r: number): Ship {
  const s: Ship = {
    id: g.nextId++,
    side, type, c, r,
    hp: UNITS[type].hp,
    moved: 0,
    done: false,
    submerged: type === 'SS',
    fought: false,
    anchored: false,
  };
  g.ships.push(s);
  return s;
}
